import { useContext } from 'react';

import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components';
import classNames from 'classnames';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext, ONBOARDING_VIDEO_KEY } from '../constants';
import { useIsOneBusinessDayAfterStart } from '../hooks';
import type { TrialStatusData } from '../types';
import { IframeMessageType, ModalType } from '../types';

interface Props {
  onClose: () => void;
  trialStatusData?: TrialStatusData;
}

export const OnboardingBanner = ({ onClose, trialStatusData }: Props) => {
  const isOneBusinessDayAfterStart =
    useIsOneBusinessDayAfterStart(trialStatusData);
  const { trackEvent } = useTrackEvent();
  const externalPatientContext = useContext(ExternalPatientContext);

  return (
    <div className="vai-absolute vai-w-[calc(100%-24px)] vai-top-3 vai-left-1/2 vai-right-1/2 vai-transform vai--translate-x-1/2 vai-z-[201]">
      <div
        className={classNames(
          'vai-relative vai-p-4 [@media(max-height:570px)]:vai-p-3 vai-bg-background-dark vai-border vai-border-zinc-500 vai-rounded-md vai-flex vai-flex-col',
          {
            'vai-pb-2 [@media(max-height:570px)]:vai-pb-0':
              isOneBusinessDayAfterStart,
          },
        )}
      >
        <FontAwesomeIcon
          onClick={() => {
            trackEvent(mixpanelEvents.externalOnboardingBannerClose, {
              ...externalPatientContext,
            });
            onClose();
          }}
          className="vai-absolute vai-top-1 vai-right-1 vai-text-white hover:vai-text-zinc-300 vai-p-2 vai-rounded-md vai-cursor-pointer vai-self-end"
          icon={faXmark}
        />
        <p className="vai-font-inter vai-text-sm vai-font-medium vai-text-zinc-50">
          Welcome to Detect AI
        </p>
        <p className="vai-mt-1 vai-font-inter vai-text-sm vai-text-zinc-400">
          <Trans>Watch this quick 1-minute video to get started.</Trans>
        </p>
        <Button
          className="vai-mt-4 [@media(max-height:570px)]:vai-mt-2 [@media(max-width:310px)]:vai-text-[12px] vai-text-wrap"
          onClick={() => {
            trackEvent(mixpanelEvents.externalOnboardingModalOpen, {
              ...externalPatientContext,
              source: 'BANNER',
            });
            window.parent.postMessage(
              {
                type: IframeMessageType.MODAL_OPEN,
                payload: {
                  type: ModalType.ONBOARDING,
                  patientId: externalPatientContext.patientId,
                  imageId: externalPatientContext.imageId,
                  externalPatientId: externalPatientContext.externalPatientId,
                  externalImageId: externalPatientContext.externalImageId,
                  source: 'BANNER',
                },
              },
              '*',
            );
          }}
        >
          <Trans>Watch the onboarding video</Trans>
        </Button>

        {/* Provide option to mark as watched only 1 business day after the start of their trial or paid subscription. */}
        {isOneBusinessDayAfterStart && (
          <Button
            className="vai-mt-1 [@media(max-height:570px)]:vai-mt-0 [@media(max-width:310px)]:vai-text-[12px] vai-text-zinc-50"
            variant="link"
            onClick={() => {
              trackEvent(mixpanelEvents.externalOnboardingVideoReportWatched, {
                ...externalPatientContext,
              });
              localStorage.setItem(ONBOARDING_VIDEO_KEY, 'true');
              onClose();
            }}
          >
            <Trans>Watched it! Don&apos;t show again.</Trans>
          </Button>
        )}
      </div>
    </div>
  );
};
